import * as React from 'react';

import * as moment from 'moment';
import styled from 'styled-components';
import CountdownClock from './CountdownClock';

import { connect } from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router-dom';

import ControlComponent from './ControlComponent';
import TimerComponent from './TimerComponent';
import WebSocketProvider from './WebSocketProvider';

import { actions as ConnectionActions , state as ConnectionState } from '../redux/modules/connection';
import { actions as LocalSettingsActions , state as LocalSettingsState } from '../redux/modules/localSettings';
import { actions as SettingsActions } from '../redux/modules/settings';
import { actions as TeamActions, selectors as TeamSelectors, state as TeamState } from '../redux/modules/team';
import { state as TimerState, selectors as TimerSelectors } from '../redux/modules/timer';
import OuchComponent from "./OuchComponent";
import {CouldNotFindTeamError} from "../api/errors";
import {TeamResponse} from "../api/responses";

const mapStateToProps = (state: { connection: ConnectionState, localSettings: LocalSettingsState, team: TeamState, timer: TimerState } ) => ({
  connection: state.connection,
  localSettings: state.localSettings,
  team: state.team,
  timer: state.timer,
});

const mapDispatchToProps = {
  ...ConnectionActions,
  ...LocalSettingsActions,
  ...TeamActions,
  ...SettingsActions,
};

interface IAppState {
  isHandlingMove: boolean;
  isTimerRunning: boolean;
  remaining:number;
  name:string;
  customDuration:number;
  couldNotJoin: boolean;
}

interface IAppExternalProps {
    match:any;
    className?:string;
}

interface IAppInternalProps {
  connection: ConnectionState;
  team: TeamState;
  timer: TimerState;
  localSettings: LocalSettingsState;

  attemptingReconnect(retry: number): void;
  connect(): void;
  connected():void;
  disconnected():void;

  fetchTeam(team: string):void;
  joinTeam(team:string, clientId: string):void;
  createTeam(team: string): Promise<TeamResponse>;

  updateSettings(settings:object):void;
  reconnectFailed():void;

  stop(): void;

  startTimer(team: string, duration: number, name: string): any;
  stopTimer(team: string):void;

  timerStarted(data: any): void;
  timerStopped(): void;

  saveLocalSettings(settings: LocalSettingsState): void;
}

type Props = IAppExternalProps & IAppInternalProps & RouteComponentProps;

class TeamComponent extends React.Component<Props, IAppState> {

    public static getDerivedStateFromProps(props: Props, state: IAppState) {
        if(props.team.lastError || props.team.lastError === "TEAM_NOT_FOUND") {
            return {
                ...state,
                couldNotJoin: true
            }
        }
        return state;
    }


  private readonly audioRef = React.createRef<HTMLAudioElement>();

  constructor(props:Props) {
    super(props);

    this.state = {
        couldNotJoin: false,
        customDuration: 0,
      isHandlingMove: false,
      isTimerRunning: false,
      name: '',
      remaining: 0,
    };

    this.clickStartTimer = this.clickStartTimer.bind(this);
    this.clickStopTimer = this.clickStopTimer.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);

    this.socketTimerStarted = this.socketTimerStarted.bind(this);
    this.socketTimerStopped = this.socketTimerStopped.bind(this);
  }


  public async componentDidMount() {
      const { team } = this.props.match.params;
        try {
            await this.props.fetchTeam(team);
        } catch(e) {
            if(e instanceof CouldNotFindTeamError) {
                console.error(e);
                // this.props.history.push("/");

                try {
                    const teamResponse = await this.props.createTeam(team);
                    this.props.history.push(`/${teamResponse.slug}`);
                } catch(e) {
                    this.props.history.push("/");
                }
            }
        }
    }

  public componentDidUpdate() {
    if (this.audioRef.current !== null
      && this.audioRef.current?.volume !== this.props.localSettings.volume) {
      this.audioRef.current.volume = this.props.localSettings.volume * 0.01;
    }
  }

    public async clickStartTimer(duration: number, name:string) {
        if(this.props.timer.isRunning) {
            await this.props.stopTimer(this.props.match.params.team);
        }
        if(this.state.customDuration !== 0) {
            duration = Math.floor(this.state.customDuration * 1000000);
        }

        this.props.startTimer(this.props.match.params.team, duration, name);
  }

  public async clickStopTimer(action: any) {
    await this.props.stopTimer(this.props.match.params.team);
    this.setState({remaining: 0, customDuration: 0 });
  }

  public handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
      if (event.buttons === 0 || TimerSelectors.isTimerRunning(this.props.timer)) {
      this.setState({ isHandlingMove: false });
        return;
    }

    this.setState({ isHandlingMove: true });
    this.handleCustomDurationInput(event.clientX, event.clientY, event.currentTarget);
  }

  public handleTouchMove(event: React.TouchEvent<HTMLDivElement>) {
    if (event.touches.length !== 1 || TimerSelectors.isTimerRunning(this.props.timer)) {
      return;
    }

    this.handleCustomDurationInput(event.touches[0].clientX, event.touches[0].clientY, event.currentTarget);
  }

  public render() {
      if(this.props.team.fetching) {
          return null;
      }

      if(this.state.couldNotJoin) {
        return (<OuchComponent/>);
      }

      const websocketHref = TeamSelectors.getWebsocketHref(this.props.team);
      const name = this.props.timer.name;

      let remaining = moment.duration(this.props.timer.remainingTime / 1000000);
      if (!this.props.timer.isRunning && this.state.customDuration > 0) {
          remaining = moment.duration(this.state.customDuration);
      }

      const formattedTime = moment.utc(remaining.asMilliseconds()).format("mm:ss");
      if (this.state.remaining > 0 && this.props.team.timer) {
          document.title = `${formattedTime} @ ${this.props.team.timer.name} - #${this.props.team.team?.name} - Tomodoro timer`;
      } else {
          document.title = `#${this.props.team.team?.name} - Tomodoro timer`;
      }


      let formattedEndTime = "";
      if(remaining.asMilliseconds() > 0) {
          formattedEndTime = moment().add(remaining, 'milliseconds').format("HH:mm");
          formattedEndTime = `bis ${formattedEndTime}`;
      }

      if(websocketHref) {
          return (
              <div className={ this.props.className } onMouseMove={this.handleMouseMove} onTouchMoveCapture={this.handleTouchMove}>
                  <WebSocketProvider
                      url={websocketHref}
                      connect={this.props.connect}
                      connected={this.props.connected}
                      disconnected={this.props.disconnected}
                      reconnectFailed={this.props.reconnectFailed}
                      timerStarted={this.socketTimerStarted}
                      timerStopped={this.socketTimerStopped}
                      attemptingReconnect={this.props.attemptingReconnect}
                  >
                      <TimerComponent name={ name } endTime={formattedEndTime}>
                          <CountdownClock hidden={ remaining.milliseconds() <= 0 } remaining={ remaining.asMilliseconds() } color={ this.getCountdownColor() }/>
                          {formattedTime}
                      </TimerComponent>
                      <ControlComponent
                          startTimer={ this.clickStartTimer }
                          stopTimer={ this.clickStopTimer }
                          disableStart={ this.props.timer.isRunning}
                          disableStop={ !this.props.timer.isRunning }
                          forceShow={ !this.props.timer.isRunning }
                          customDuration={ Math.round(this.state.customDuration) }
                          team={this.props.team}
                      />
                </WebSocketProvider>
                <audio ref={ this.audioRef } src={ '/sounds/' + this.props.localSettings.alarmSound } />
              </div>
          );
      }

      return null;
  }

  private socketTimerStarted(data: any): void {
      if(!TimerSelectors.isTimerRunning(this.props.timer)) {
          this.props.timerStarted(data);
      }
  }

  private socketTimerStopped(): void {
      this.props.fetchTeam(this.props.team.team!.name);
      this.setState({remaining: 0, customDuration: 0});
      this.audioRef.current?.play();
  }

  private getCountdownColor(): string {
    if(this.props.team.team!.timer) {
        return this.props.team.team!.timer!.name === 'Fokusphase' ? 'red': 'green';
    }
    return 'blue';
  }

  private handleCustomDurationInput(clientX:number, clientY:number, element:HTMLDivElement):void {
    const rect = element.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const dx = x - rect.width / 2;
    const dy = y - rect.height / 2;

    const angle = Math.PI + Math.atan2(dy, dx);
    const minutes = angle / 2 / Math.PI * 60 + 45;
    const customDuration = (60 - minutes % 60) * 60000;
    this.setState({ ...this.state, customDuration });
  }
}

export default connect(mapStateToProps, mapDispatchToProps)
(styled(withRouter(TeamComponent))`
    display: flex;

    flex-grow: 1;

    text-align: center;
    padding: 20px;

    flex-direction: column;
    justify-content: space-between;
`);



// WEBPACK FOOTER //
// ./src/components/TeamComponent.tsx